/**
 * CollectorDetailModal — Modal de detalle / comparación
 *
 * Vistas (CC ↔ CR):
 * - ok/descuadre: comparación lado a lado CC (EntityPanel) ↔ CRs (CollectionRecordsPanel
 *   con resumen + tabla de records individuales). Grid 2:3.
 * - soloCC/soloCR: detalle genérico con secciones.
 */

import { useState } from 'react';
import { X, Copy, Check, Code, Eye, FileText, Database } from 'lucide-react';
import {
  type TabEntityConfig,
  type ReconciliationCRDetail,
  RECONCILIATION_CONFIG,
  SUMMARY_COLUMNS,
} from '@/types/collector-reconciliation.types';
import { ConstanciaCard } from './ConstanciaCard';

interface Props {
  item: Record<string, unknown>;
  statusKey: string;
  onClose: () => void;
  /** Override reconciliation config (default: RECONCILIATION_CONFIG for CC↔CR) */
  config?: typeof RECONCILIATION_CONFIG;
  /** Override summary columns (default: SUMMARY_COLUMNS for CC↔CR) */
  summaryColumns?: typeof SUMMARY_COLUMNS;
}

// ─── Format helpers ──────────────────────────────────────────────────────────

function fmtValue(value: unknown, format?: string): string {
  if (value === null || value === undefined) return '-';
  if (format === 'currency') {
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (isNaN(num)) return String(value);
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(num);
  }
  if (format === 'currency_usd') {
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (isNaN(num)) return String(value);
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  }
  if (format === 'date') {
    if (typeof value !== 'string') return String(value);
    try {
      return new Date(value).toLocaleDateString('es-MX', {
        day: 'numeric', month: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    } catch {
      return String(value);
    }
  }
  if (format === 'percent') {
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    return isNaN(num) ? String(value) : `${(num * 100).toFixed(2)}%`;
  }
  return String(value);
}

const STATUS_COLORS: Record<string, string> = {
  ORIGINADO: 'text-amber-400',
  COMPRADO: 'text-purple-400',
  COBRADO: 'text-blue-400',
  SALDADO: 'text-green-400',
  COMPLETED: 'text-green-400',
  PAID: 'text-green-400',
};

function getStatusColor(status: unknown): string {
  return STATUS_COLORS[String(status)] ?? 'text-amber-400';
}

// ─── Panel color sets ────────────────────────────────────────────────────────

interface PanelColorSet {
  border: string;
  bg: string;
  headerBorder: string;
  text: string;
}

const COLORS: Record<string, PanelColorSet> = {
  blue: { border: 'border-blue-500/30', bg: 'bg-blue-500/5', headerBorder: 'border-blue-500/20', text: 'text-blue-400' },
  purple: { border: 'border-purple-500/30', bg: 'bg-purple-500/5', headerBorder: 'border-purple-500/20', text: 'text-purple-400' },
  teal: { border: 'border-teal-500/30', bg: 'bg-teal-500/5', headerBorder: 'border-teal-500/20', text: 'text-teal-400' },
  green: { border: 'border-green-500/30', bg: 'bg-green-500/5', headerBorder: 'border-green-500/20', text: 'text-green-400' },
  amber: { border: 'border-amber-500/30', bg: 'bg-amber-500/5', headerBorder: 'border-amber-500/20', text: 'text-amber-400' },
  cyan: { border: 'border-cyan-500/30', bg: 'bg-cyan-500/5', headerBorder: 'border-cyan-500/20', text: 'text-cyan-400' },
  slate: { border: 'border-slate-500/30', bg: 'bg-slate-500/5', headerBorder: 'border-slate-500/20', text: 'text-slate-400' },
  red: { border: 'border-red-500/30', bg: 'bg-red-500/5', headerBorder: 'border-red-500/20', text: 'text-red-400' },
};

// ─── Comparison view (EntityPanel × 2) ──────────────────────────────────────

const ENTITY_ICONS: Record<string, typeof FileText> = {
  'CreditContract': Database,
  'CollectionRecords': FileText,
  'Transferencia declarada': Database,
  'Fondeo declarado': Database,
  'Par bancario FSA -> FMX': FileText,
  'Par bancario FMX -> FSA': FileText,
};

function EntityPanel({
  entity, item, onViewAll,
}: {
  entity: TabEntityConfig;
  item: Record<string, unknown>;
  onViewAll: () => void;
}) {
  const c = COLORS[entity.color] ?? COLORS.blue!;
  const Icon = ENTITY_ICONS[entity.name] ?? Database;

  return (
    <div className={`rounded-lg border ${c.border} ${c.bg}`}>
      <div className={`flex items-center justify-between border-b p-3 ${c.headerBorder}`}>
        <div className="flex items-center gap-2">
          <Icon size={16} className={c.text} />
          <span className="text-sm font-medium text-foreground">{entity.name}</span>
        </div>
        <button type="button" onClick={onViewAll} className={`flex items-center gap-1 text-xs ${c.text} hover:opacity-80`}>
          <Eye size={12} /> Ver todo
        </button>
      </div>
      <div className="space-y-2 p-3 text-xs">
        {entity.fields.map((field) => {
          const value = item[field.key];
          const isStatus = field.label.toLowerCase().includes('status');
          const isMono = field.format === 'mono';
          return (
            <div key={field.key} className="flex items-baseline justify-between gap-2">
              <span className="text-muted-foreground">{field.label}:</span>
              <span className={`text-right ${
                isStatus ? getStatusColor(value) :
                (field.format === 'currency' || field.format === 'currency_usd') ? 'font-medium text-green-400' :
                isMono ? 'font-mono text-[10px] text-foreground' : 'text-foreground'
              }`}>
                {isMono ? String(value ?? '-') : fmtValue(value, field.format)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── CollectionRecords panel: resumen + tabla de records ────────────────────

const PAYMENT_TYPE_COLOR: Record<string, string> = {
  REGULAR: 'text-foreground',
  ADVANCED: 'text-blue-400',
  ANTICIPATED: 'text-amber-400',
  SETTLEMENT: 'text-green-400',
};

const COLLECTION_STATUS_COLOR: Record<string, string> = {
  FULL: 'text-green-400',
  PARTIAL: 'text-amber-400',
  NONE: 'text-red-400',
};

function CollectionRecordsPanel({
  item, onViewAll,
}: {
  item: Record<string, unknown>;
  onViewAll: () => void;
}) {
  const c = COLORS.teal!;
  const records = (item.records as ReconciliationCRDetail[] | undefined) ?? [];
  const cantidad = item.cantidadCRs as number | undefined;
  const suma = item.sumaColectadaCR as number | undefined;
  const diff = item.diferencia as number | undefined;

  return (
    <div className={`rounded-lg border ${c.border} ${c.bg}`}>
      <div className={`flex items-center justify-between border-b p-3 ${c.headerBorder}`}>
        <div className="flex items-center gap-2">
          <FileText size={16} className={c.text} />
          <span className="text-sm font-medium text-foreground">CollectionRecords</span>
        </div>
        <button type="button" onClick={onViewAll} className={`flex items-center gap-1 text-xs ${c.text} hover:opacity-80`}>
          <Eye size={12} /> Ver todo
        </button>
      </div>

      {/* Resumen */}
      <div className="space-y-2 border-b border-border/50 p-3 text-xs">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-muted-foreground"># Records:</span>
          <span className="text-foreground">{fmtValue(cantidad)}</span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-muted-foreground">Suma Colectada:</span>
          <span className="font-medium text-green-400">{fmtValue(suma, 'currency')}</span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-muted-foreground">Diferencia:</span>
          <span className={`font-medium ${diff && diff !== 0 ? 'text-amber-400' : 'text-green-400'}`}>
            {fmtValue(diff, 'currency')}
          </span>
        </div>
      </div>

      {/* Tabla de records individuales */}
      {records.length === 0 ? (
        <div className="p-3 text-center text-xs text-muted-foreground">
          Sin records individuales
        </div>
      ) : (
        <div className="max-h-64 overflow-auto">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-card/95 backdrop-blur">
              <tr className="border-b border-border/40 text-left text-muted-foreground">
                <th className="px-2 py-1.5 font-medium">Cup #</th>
                <th className="px-2 py-1.5 font-medium">Tipo</th>
                <th className="px-2 py-1.5 text-right font-medium">Monto</th>
                <th className="px-2 py-1.5 font-medium">Status</th>
                <th className="px-2 py-1.5 font-medium">Fecha</th>
                <th className="px-2 py-1.5 font-medium">Referencia</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, idx) => (
                <tr key={r.record_id ?? `${r.credit_id}-${r.coupon_number}-${idx}`} className="border-b border-border/20">
                  <td className="px-2 py-1 tabular-nums text-foreground">{r.coupon_number}</td>
                  <td className={`px-2 py-1 font-medium ${PAYMENT_TYPE_COLOR[r.payment_type ?? ''] ?? 'text-foreground'}`}>
                    {r.payment_type ?? '-'}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums font-medium text-green-400">
                    {fmtValue(r.amount_collected_mxn, 'currency')}
                  </td>
                  <td className={`px-2 py-1 font-medium ${COLLECTION_STATUS_COLOR[r.collection_status] ?? 'text-foreground'}`}>
                    {r.collection_status}
                  </td>
                  <td className="px-2 py-1 text-muted-foreground">
                    {r.collection_date ? fmtValue(r.collection_date, 'date').split(',')[0] : '-'}
                  </td>
                  <td className="px-2 py-1 font-mono text-[10px] text-foreground" title={r.reference ?? ''}>
                    {r.reference ? `${r.reference.slice(0, 16)}${r.reference.length > 16 ? '…' : ''}` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ComparisonBody({ item, config }: { item: Record<string, unknown>; config: typeof RECONCILIATION_CONFIG }) {
  const [jsonTarget, setJsonTarget] = useState<'a' | 'b' | null>(null);
  const entityAKeys = new Set(config.entityA.fields.map((f) => f.key));
  const entityBKeys = new Set(config.entityB.fields.map((f) => f.key));
  const entityAData: Record<string, unknown> = {};
  const entityBData: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(item)) {
    if (entityAKeys.has(k)) entityAData[k] = v;
    if (entityBKeys.has(k)) entityBData[k] = v;
  }
  // El panel B (CollectionRecords) tambien consume `records[]` aunque no este en el config
  if ('records' in item) entityBData.records = item.records;

  if (jsonTarget) {
    const data = jsonTarget === 'a' ? entityAData : entityBData;
    const name = jsonTarget === 'a' ? config.entityA.name : config.entityB.name;
    return (
      <div>
        <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
          <button type="button" onClick={() => setJsonTarget(null)} className="text-primary hover:underline">← Volver</button>
          <span>{name}</span>
        </div>
        <pre className="max-h-96 overflow-auto rounded-md bg-muted p-4 font-mono text-xs">
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    );
  }

  // El flujo Collector CC<->CR tiene tabla de records a la derecha (grid 2:3).
  // Los flujos FSA/PCF (cross-account) no tienen records — muestran par bancario
  // salida/entrada como EntityPanel simetrico (grid 1:1). Constancia CP solo
  // aplica al flujo Collector.
  const isCollectorFlow = config.entityB.name === 'CollectionRecords';
  const ccId = String(item.ccId ?? '');
  const constanciaS3Key = (item.ccConstanciaS3Key as string | null | undefined) ?? null;
  const constanciaHash = (item.ccConstanciaHash as string | null | undefined) ?? null;
  const ccCreateDate = (item.ccCreateDate as string | undefined) ?? '';

  return (
    <div className="space-y-4">
      {isCollectorFlow ? (
        <div className="grid grid-cols-5 gap-4">
          <div className="col-span-2">
            <EntityPanel entity={config.entityA} item={item} onViewAll={() => setJsonTarget('a')} />
          </div>
          <div className="col-span-3">
            <CollectionRecordsPanel item={item} onViewAll={() => setJsonTarget('b')} />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <EntityPanel entity={config.entityA} item={item} onViewAll={() => setJsonTarget('a')} />
          <EntityPanel entity={config.entityB} item={item} onViewAll={() => setJsonTarget('b')} />
        </div>
      )}
      {isCollectorFlow && ccId && (
        <ConstanciaCard
          ccId={ccId}
          constanciaS3Key={constanciaS3Key}
          constanciaHash={constanciaHash}
          ccCreateDate={ccCreateDate}
        />
      )}
    </div>
  );
}

// ─── Sections view ──────────────────────────────────────────────────────────

const SECTION_DEFS: { label: string; color: string; keys: string[] }[] = [
  { label: 'ESTADO Y FECHAS', color: 'blue', keys: ['ccStatus', 'collectionStatus', 'ccCreateDate', 'createDate', 'created_at', 'updated_at'] },
  { label: 'MONTOS', color: 'teal', keys: ['ccCreditNotional', 'ccCreditTotal', 'ccCreditFee', 'ccCreditTax', 'sumaColectadaCR', 'diferencia', 'amountCollectedMxn', 'cantidadCRs'] },
  { label: 'RELACIONES', color: 'purple', keys: ['ccRelatedTransaction', 'creditContractId', 'creditId', 'couponNumber'] },
  { label: 'IDENTIFICADORES', color: 'blue', keys: ['ccId', 'ccTenantId', 'ccDirectoryId'] },
  { label: 'CONFIGURACIÓN', color: 'purple', keys: ['ccCollectionType', 'paymentType', 'provider'] },
];

const DATE_KEYS = new Set(['ccCreateDate', 'createDate', 'created_at', 'updated_at']);
const AMOUNT_KEYS = new Set(['ccCreditNotional', 'ccCreditTotal', 'ccCreditFee', 'ccCreditTax', 'sumaColectadaCR', 'diferencia', 'amountCollectedMxn']);
const ID_KEYS = new Set(['ccId', 'creditId', 'creditContractId', 'ccRelatedTransaction', 'ccTenantId', 'ccDirectoryId']);

function inferFormat(key: string): string | undefined {
  if (DATE_KEYS.has(key)) return 'date';
  if (AMOUNT_KEYS.has(key)) return 'currency';
  if (ID_KEYS.has(key)) return 'mono';
  return undefined;
}

function humanizeKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}

function SectionsBody({ item }: { item: Record<string, unknown> }) {
  const used = new Set<string>();
  const sections: { label: string; color: string; fields: { key: string; value: unknown; format?: string }[] }[] = [];

  for (const def of SECTION_DEFS) {
    const fields: typeof sections[0]['fields'] = [];
    for (const key of def.keys) {
      if (key in item) {
        const fmt = inferFormat(key);
        fields.push({ key, value: item[key], ...(fmt ? { format: fmt } : {}) });
        used.add(key);
      }
    }
    if (fields.length > 0) sections.push({ label: def.label, color: def.color, fields });
  }

  const rest: typeof sections[0]['fields'] = [];
  for (const [key, value] of Object.entries(item)) {
    if (!used.has(key)) {
      const fmt = inferFormat(key);
      rest.push({ key, value, ...(fmt ? { format: fmt } : {}) });
    }
  }
  if (rest.length > 0) sections.push({ label: 'OTROS', color: 'blue', fields: rest });

  return (
    <div className="space-y-3">
      {sections.map((section) => {
        const c = COLORS[section.color] ?? COLORS.blue!;
        return (
          <div key={section.label} className={`rounded-lg border ${c.border} ${c.bg} p-3`}>
            <h4 className={`mb-2 text-[10px] font-bold uppercase tracking-wider ${c.text}`}>{section.label}</h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
              {section.fields.map(({ key, value, format }) => {
                const isStatus = key.toLowerCase().includes('status');
                const isAmount = format === 'currency' || format === 'currency_usd';
                return (
                  <div key={key}>
                    <span className="text-muted-foreground">{humanizeKey(key)}:</span>
                    <div className={`font-mono text-[11px] ${
                      isStatus ? `font-semibold ${getStatusColor(value)}` :
                      isAmount ? 'font-semibold text-green-400' : 'text-foreground'
                    }`}>
                      {value === null || value === undefined ? '-' :
                       isAmount ? fmtValue(value, format) :
                       format === 'date' ? fmtValue(value, 'date') :
                       typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main modal ──────────────────────────────────────────────────────────────

export function CollectorDetailModal({ item, statusKey, onClose, config: configProp, summaryColumns: colsProp }: Props) {
  const [showJson, setShowJson] = useState(false);
  const [copied, setCopied] = useState(false);

  const cfg = configProp ?? RECONCILIATION_CONFIG;
  const cols = colsProp ?? SUMMARY_COLUMNS;

  const jsonStr = JSON.stringify(item, null, 2);
  const modalWidth = cfg.matchStatuses.includes(statusKey) ? 'w-[1200px]' : 'w-[750px]';
  const colLabel = cols.find((c) => c.key === statusKey)?.label ?? statusKey;
  const mainId = String(item.id ?? item.ccId ?? item.creditId ?? item.transferId ?? '');
  const { title, subtitle } = cfg.matchStatuses.includes(statusKey)
    ? { title: `Comparacion ${cfg.label}`, subtitle: cfg.description }
    : { title: colLabel, subtitle: mainId };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(jsonStr);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className={`flex max-h-[85vh] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-xl ${modalWidth}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border p-4">
          <div>
            <h3 className="font-medium text-foreground">{title}</h3>
            {subtitle && <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4">
          {showJson ? (
            <div>
              <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                <button type="button" onClick={() => setShowJson(false)} className="text-primary hover:underline">
                  ← Volver
                </button>
                <span>JSON completo</span>
              </div>
              <pre className="max-h-96 overflow-auto rounded-md bg-muted p-4 font-mono text-xs">
                {jsonStr}
              </pre>
            </div>
          ) : cfg.matchStatuses.includes(statusKey) ? (
            <ComparisonBody item={item} config={cfg} />
          ) : (
            <SectionsBody item={item} />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border p-4">
          <button
            type="button"
            onClick={() => setShowJson((v) => !v)}
            className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Code size={14} />
            {showJson ? 'Ver campos' : 'Ver JSON'}
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copiado' : 'Copiar JSON'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
